import React, { useRef, useLayoutEffect } from 'react';
import { useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
//
import { Link, Container, Box, Typography, Alert, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import { useTheme } from "@mui/material";
import { useTranslation } from 'react-i18next';

//
import { Section, SectionItem } from '/@/app/views/LabTestView/Section';
import { route } from '/@/app/routes';
import { AppContext } from '/@/app/AppContext';
import { ScrollContext } from '/@/app/AppLayout';
import { HelpingDrawerLayout } from '/@/app/views/common/HelpingDrawerLayout';
import { ProfdataImportBar } from '/@/app/components/ProfdataImportBar';
import { StepInfoBlock } from '/@/app/components/StepInfoBlock';
import { AudioPlayer } from '/@/app/components/AudioPlayer';
import { MeasurementModal } from '/@/app/views/LabTestView/MeasurementModal';
import { FloatingSaveControls } from '/@/app/components/FloatingSaveControls';
import { MeasurementModalState, MeasurementModalStateContext } from '/@/app/views/LabTestView/MeasurementModalState';
import { TableOfContents } from '/@/app/views/LabTestView/TableOfContents';

const FloatingSaveControlsObserver=observer(FloatingSaveControls);

const TopIcons = ({sx}) => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const { help } = useContext(AppContext).bootstrapData;
    let { showDynamicsPage } = useContext(AppContext);

    const topIconsSize = "40px";

    return (
        <Box sx={{
            display: "flex",
            flexFlow: "row nowrap",
            gap: "15px",
            mr: 1, ml: 2, mb: 1,
            ...sx,
        }}>
            { showDynamicsPage &&
                <Box sx={{ display: "flex", flexFlow: "column nowrap", alignItems: "center", gap: "0px", color: theme.palette.primary.main }}>
                    <Link component={RouterLink} to={route('/dynamics/')} sx={{ lineHeight: "18px" }}>
                        <InsightsIcon sx={{ fontSize: topIconsSize, color: theme.palette.primary.main }} />
                    </Link>
                    <Typography variant="caption" sx={{ fontSize: "10px" }}>динамика</Typography>
                </Box>
            }
            <Box sx={{ display: "flex", flexFlow: "column nowrap", alignItems: "center", gap: "0px", color: theme.palette.primary.main }}>
                <Link component={RouterLink} to={route('/panels/')} sx={{ lineHeight: "18px" }}>
                    <AutoAwesomeMotionOutlinedIcon sx={{ fontSize: topIconsSize, color: theme.palette.primary.main }} />
                </Link>
                <Typography variant="caption" sx={{ fontSize: "10px" }}>панели</Typography>
            </Box>
            <AudioPlayer audioSrc={help.sounds['labtests-'+i18n.language]} size={topIconsSize} sx={{ }} />
        </Box>
    );
}

const floatingPanelOffset = 80;

const TopOffset = observer(({ state }) => {
    return (
        <Box
            sx={{
                pt: state.saveState === "unsaved" ? floatingPanelOffset+"px" : 0,
            }}
        ></Box>
    )

});

export const LabTestsView = observer(() => {
    const state = useContext(AppContext).labTestsSectionListState;
    const panel = useContext(AppContext).bootstrapData.panel;
    const { featureEnabled } = useContext(AppContext);
    const { payment_system_enabled } = useContext(AppContext).bootstrapData;
    const { i18n } = useTranslation();
    const { help } = useContext(AppContext).bootstrapData;

const {restoreScroll}=useContext(ScrollContext);
useLayoutEffect(() => { restoreScroll(); }, []);

    // modal state owned by the component
    const modalState = new MeasurementModalState();

    // Prepare Table of Content anchors
    const refs = state.sections.map(() => useRef(null));

    // closure to refs, the other props will be passed by the HelpingDrawerLayout
    const DrawerContent = ({drawerOpen, setDrawerOpen}) => {
        return <TableOfContents sectionElementRefs={refs} closeDrawer={() => setDrawerOpen(false)} />
    }

    let emptyFieldsWarning = false;
    if (panel.derived_from !== null && panel.derived_from.imported_at !== null) {
        emptyFieldsWarning = true;
        const importedAt = panel.derived_from.imported_at
        emptyFieldsWarning = dayjs().isBefore(dayjs(importedAt).add(24, 'hours'))
    }
    return (
        <HelpingDrawerLayout
            drawerContent={DrawerContent}
            buttonText="Разделы"
            // customized smaller sidebar
            sidebarWidth={{ xs: "0", lg: "300px" }}
            drawerWidth={{ xs: "100vw", sm: "300px" }}
        >
            <Container maxWidth="md">
                <TopOffset state={state} />

                <FloatingSaveControlsObserver saveState={state.saveState} onReset={()=>{state.resetUnsaved()}} onSave={()=>{state.saveAll()}}/>

                <Box sx={{ display: "flex", flexFlow: "row wrap", justifyContent: "space-between" }}>
                    <Typography variant="h3">Анализы и показатели</Typography>
                    <TopIcons sx={{ flexGrow: 1, justifyContent: "flex-end" }}/>
                </Box>

                <ProfdataImportBar sx={{ mb: 3 }} />

                {/* this block simply becomes obsolete in 2025 */}
                {(!payment_system_enabled && !featureEnabled('extra_lab_tests_analytics')) &&
                    <Alert severity="error" sx={{ mb: 3 }}>
                        Полный доступ ко всем анализам (часть анализов скрыта)
                        возможен, если
                        результаты анализов с названием "Тераностика"
                        загружаются по номеру заявки из лабораторий
                        ДНКОМ или Aqua Lab хотя бы раз
                        в 12 месяцев.
                    </Alert>
                }

                {emptyFieldsWarning &&
                    <Alert severity="info" sx={{ mb: 3 }}>
                        После импорта результатов ваших анализов, многие окошки в разделе анализов останутся пустыми.
                        Так и должно быть, так как вы не делали все анализы на свете, а только те, которые необходимы.
                    </Alert>
                }

                <MeasurementModalStateContext.Provider value={modalState}>
                    <Grid container spacing={2}>
                        {state.sections.map((state, index) => (
                            <Grid item xs={12} key={index}>
                                <SectionItem ref={refs[index]} >
                                    <Section section={state} />
                                </SectionItem>
                            </Grid>
                        ))}
                    </Grid>
                </MeasurementModalStateContext.Provider>

                <StepInfoBlock text={ help.texts['lab_tests-footer-'+i18n.language] } />
                <MeasurementModal modalState={modalState} />
            </Container>
        </HelpingDrawerLayout>
    );
});
